// TeamDetail — per-role section view for one team (PRODOPSAI-531;
// per-role sections matching the PDF org chart in PRODOPSAI-543). Header
// (monogram tile + name + meta row) and one section per PDF team role.
// A role filter narrows to a single role. Read-only.

import { useMemo, useState } from 'react';
import { Button, ChevronLeftIcon } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { ITeamMembershipTreeTeam } from '@skystone/shared';
import { TeamRoleSection } from './TeamRoleSection';
import { memberCountLabel } from './team-card-helpers';
import {
  engineeringManagerInitials,
  groupByRole,
  roleCountLabel,
} from './team-detail-helpers';
import { teamAccent, teamMonogram } from './team-identity';

interface TeamDetailProps {
  team: ITeamMembershipTreeTeam;
  onBack: () => void;
}

const ALL_ROLES = '__all__';

export function TeamDetail({ team, onBack }: Readonly<TeamDetailProps>) {
  const { name, regionName } = team.team;
  const accent = teamAccent(regionName);
  const monogram = teamMonogram(name);

  const sections = useMemo(() => groupByRole(team.roleGroups), [team.roleGroups]);
  const [roleFilter, setRoleFilter] = useState<string>(ALL_ROLES);

  // Guard against a stale filter when the selected role isn't present for
  // this team (e.g. after switching teams) — fall back to showing all.
  const effectiveFilter = sections.some((s) => s.id === roleFilter)
    ? roleFilter
    : ALL_ROLES;

  const visibleSections =
    effectiveFilter === ALL_ROLES
      ? sections
      : sections.filter((s) => s.id === effectiveFilter);

  const emInitials = engineeringManagerInitials(team.roleGroups);
  const hasRegion = typeof regionName === 'string' && regionName.trim() !== '';

  return (
    <div className="space-y-5" data-testid="team-detail">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ChevronLeftIcon className="mr-1 h-4 w-4" />
        Back to all teams
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'flex h-12 w-12 flex-none items-center justify-center rounded-xl text-lg font-bold',
              accent.tileBg,
              accent.tileText,
            )}
            aria-hidden="true"
          >
            {monogram}
          </span>
          <div>
            <h2 className="text-xl font-bold text-foreground">{name}</h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span>{memberCountLabel(team.memberCount)}</span>
              <span aria-hidden="true">·</span>
              <span>{roleCountLabel(team.roleGroups.length)}</span>
              {emInitials !== null && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>EM: {emInitials}</span>
                </>
              )}
              {hasRegion && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>Region: {regionName}</span>
                </>
              )}
            </p>
          </div>
        </div>

        {sections.length > 0 && (
          <select
            value={effectiveFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label="Filter roles"
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value={ALL_ROLES}>All roles</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {team.roleGroups.length === 0 ? (
        <p className="text-sm text-muted-foreground">No members mapped yet.</p>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          data-testid="role-sections"
        >
          {visibleSections.map((section) => (
            <TeamRoleSection
              key={section.id}
              section={section}
              badgeBg={accent.badgeBg}
              badgeText={accent.badgeText}
            />
          ))}
        </div>
      )}
    </div>
  );
}
